// src/pages/Onboarding.jsx
import { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { Check, ChevronRight, SkipForward, Sparkles, ArrowLeft } from 'lucide-react';
import toast from 'react-hot-toast';

// ── Step config ────────────────────────────────────────────────────────────────
const USE_CASES = [
  { id: 'publisher',  label: 'Publisher / Content creator', desc: 'Track AdSense, Ad Manager revenue',       emoji: '📊' },
  { id: 'ecommerce',  label: 'Ecommerce / Shopify',         desc: 'Track store revenue and ad ROAS',         emoji: '🛍️' },
  { id: 'marketer',   label: 'Digital marketer',            desc: 'Track ad spend across platforms',         emoji: '🎯' },
  { id: 'agency',     label: 'Agency / Freelancer',         desc: 'Manage multiple clients',                 emoji: '🏢' },
  { id: 'hapiguard',  label: 'Protect ad revenue',          desc: 'Block invalid clicks on AdSense & GPT',   emoji: '🛡️' },
];

const ROLES = [
  { id: 'solo',    label: 'Solo operator',     desc: 'Just me running everything' },
  { id: 'team',    label: 'Small team',         desc: '2–10 people' },
  { id: 'agency',  label: 'Agency',             desc: 'Managing client accounts' },
  { id: 'startup', label: 'Startup / Scale-up', desc: 'Growing fast' },
];

const ALL_PLATFORMS = [
  { id: 'meta_ads',   label: 'Meta Ads',            desc: 'Facebook & Instagram',        emoji: '📘', group: 'Ad Spend',   for: ['marketer', 'ecommerce', 'agency'] },
  { id: 'google_ads', label: 'Google Ads',           desc: 'Search & display campaigns',  emoji: '🎯', group: 'Ad Spend',   for: ['marketer', 'ecommerce', 'agency'] },
  { id: 'tiktok_ads', label: 'TikTok Ads',           desc: 'TikTok Business',             emoji: '🎵', group: 'Ad Spend',   for: ['marketer', 'ecommerce', 'agency'] },
  { id: 'adsense',    label: 'Google AdSense',       desc: 'Publisher ad revenue',        emoji: '💰', group: 'Publisher',  for: ['publisher', 'agency'] },
  { id: 'ad_manager', label: 'Google Ad Manager',    desc: 'Premium publisher',           emoji: '📈', group: 'Publisher',  for: ['publisher', 'agency'] },
  { id: 'shopify',    label: 'Shopify',              desc: 'Ecommerce store data',         emoji: '🛍️', group: 'Ecommerce',  for: ['ecommerce', 'agency'] },
  { id: 'hapiguard',  label: 'HapiGuard',            desc: 'Invalid click protection',    emoji: '🛡️', group: 'Protection', for: ['publisher', 'agency', 'hapiguard'] },
];

const EXPERIENCE = [
  { id: 'beginner',      label: 'Beginner',      desc: "I'm just getting started with ad tracking" },
  { id: 'intermediate',  label: 'Intermediate',  desc: "I've used analytics tools before"           },
  { id: 'advanced',      label: 'Advanced',       desc: 'I know my ROAS from my CPA'                },
];

// ── Shared UI components ───────────────────────────────────────────────────────
function SelectCard({ selected, onClick, children, multi }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full text-left p-4 rounded-xl border-2 transition-all ${
        selected
          ? 'border-primary bg-primary/5 shadow-sm'
          : 'border-border hover:border-primary/40 hover:bg-accent/40'
      }`}
    >
      <div className="flex items-start gap-3">
        {children}
        <div className={`ml-auto flex-shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${
          selected ? 'bg-primary border-primary' : 'border-muted-foreground/30'
        }`}>
          {selected && <Check className="w-3 h-3 text-white" />}
        </div>
      </div>
    </button>
  );
}

function StepHeader({ step, total, title, subtitle }) {
  return (
    <div className="mb-8">
      <div className="flex items-center gap-2 mb-4">
        {Array.from({ length: total }).map((_, i) => (
          <div key={i} className={`h-1 flex-1 rounded-full transition-colors ${i < step ? 'bg-primary' : 'bg-border'}`} />
        ))}
      </div>
      <p className="text-xs text-muted-foreground mb-1">Step {step} of {total}</p>
      <h2 className="text-2xl font-bold">{title}</h2>
      {subtitle && <p className="text-muted-foreground mt-1">{subtitle}</p>}
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────
export default function Onboarding() {
  const { user, profile, refreshProfile } = useAuth();
  const navigate = useNavigate();

  const [step, setStep]                     = useState(1); // 1-5
  const [useCases, setUseCases]             = useState([]);
  const [role, setRole]                     = useState('');
  const [platforms, setPlatforms]           = useState([]);
  const [experience, setExperience]         = useState('');
  const [saving, setSaving]                 = useState(false);
  const [loading, setLoading]               = useState(true);

  const TOTAL_STEPS = 5;

  // Redirect if already completed — uses maybeSingle() so missing row doesn't throw
  useEffect(() => {
    if (!user) return;

    async function checkProgress() {
      try {
        // Check profiles flag first (fast)
        const { data: prof } = await supabase
          .from('profiles')
          .select('onboarding_completed')
          .eq('id', user.id)
          .maybeSingle();

        // Check onboarding_progress row
        const { data } = await supabase
          .from('onboarding_progress')
          .select('completed_at,use_cases,primary_role,selected_platforms,experience_level')
          .eq('user_id', user.id)
          .maybeSingle();

        // Only redirect if BOTH flags confirm completion
        // (avoids redirecting new users who have a stale profiles.onboarding_completed=true)
        if (data?.completed_at && prof?.onboarding_completed) {
          navigate('/dashboard');
          return;
        }

        // Restore partial progress if any
        if (data?.use_cases?.length)         setUseCases(data.use_cases);
        if (data?.primary_role)               setRole(data.primary_role);
        if (data?.selected_platforms?.length) setPlatforms(data.selected_platforms);
        if (data?.experience_level)           setExperience(data.experience_level);
      } catch (e) {
        console.error('[Onboarding] progress check error:', e.message);
      } finally {
        setLoading(false);
      }
    }

    checkProgress();
  }, [user, navigate]);

  // Auto-suggest platforms based on use cases
  useEffect(() => {
    if (step === 3 && platforms.length === 0 && useCases.length > 0) {
      const suggested = ALL_PLATFORMS.filter(p => p.for.some(f => useCases.includes(f))).map(p => p.id);
      setPlatforms(suggested);
    }
  }, [step]);

  const toggleUseCase = (id) => setUseCases(v => v.includes(id) ? v.filter(x => x !== id) : [...v, id]);
  const togglePlatform = (id) => setPlatforms(v => v.includes(id) ? v.filter(x => x !== id) : [...v, id]);

  // Save survey data to supabase
  const saveSurvey = async () => {
    const surveyData = { use_cases: useCases, primary_role: role, selected_platforms: platforms, experience_level: experience, survey_completed_at: new Date().toISOString() };
    await supabase.from('onboarding_progress').upsert({ user_id: user.id, ...surveyData }, { onConflict: 'user_id' });
    // Denormalise onto profiles for admin reporting
    await supabase.from('profiles').update({ onboarding_use_cases: useCases, onboarding_role: role, onboarding_platforms: platforms, onboarding_experience: experience }).eq('id', user.id);
  };

  const handleNext = async () => {
    if (step === 1 && useCases.length === 0) { toast.error('Select at least one option'); return; }
    if (step === 2 && !role)                  { toast.error('Tell us your role'); return; }
    if (step === 3 && platforms.length === 0) { toast.error('Select at least one platform'); return; }
    if (step === 4 && !experience)            { toast.error('Select your experience level'); return; }

    if (step === 4) {
      setSaving(true);
      await saveSurvey();
      setSaving(false);
    }
    setStep(s => s + 1);
  };

  const handleFinish = async () => {
    setSaving(true);
    try {
      await supabase.from('onboarding_progress').upsert({ user_id: user.id, completed_at: new Date().toISOString() }, { onConflict: 'user_id' });
      await supabase.from('profiles').update({ onboarding_completed: true }).eq('id', user.id);
      await refreshProfile();
      navigate('/dashboard');
    } catch (e) { toast.error('Something went wrong'); }
    setSaving(false);
  };

  const handleSkipAll = async () => {
    await supabase.from('onboarding_progress').upsert({ user_id: user.id, completed_at: new Date().toISOString() }, { onConflict: 'user_id' });
    await supabase.from('profiles').update({ onboarding_completed: true }).eq('id', user.id);
    await refreshProfile();
    navigate('/dashboard');
  };

  const platformSetupPath = (id) => ({
    meta_ads:   '/meta-ads',
    google_ads: '/google-ads',
    tiktok_ads: '/tiktok-ads',
    adsense:    '/adsense',
    ad_manager: '/ad-manager',
    shopify:    '/shopify',
    hapiguard:  '/hapiguard',
  })[id] || '/dashboard';

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-background to-background">
      {/* Topbar */}
      <div className="border-b bg-background/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary text-primary-foreground font-bold text-sm">R</div>
            <span className="text-xl font-bold">RevHapi</span>
          </Link>
          <button onClick={handleSkipAll} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors">
            Skip setup <SkipForward className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-6 py-12">

        {/* ── Step 1: Use case ── */}
        {step === 1 && (
          <div>
            <StepHeader step={1} total={TOTAL_STEPS} title="What brings you to RevHapi?" subtitle="Select everything that applies — you can track multiple things." />
            <div className="grid gap-3 sm:grid-cols-2">
              {USE_CASES.map(uc => (
                <SelectCard key={uc.id} selected={useCases.includes(uc.id)} onClick={() => toggleUseCase(uc.id)} multi>
                  <span className="text-2xl flex-shrink-0">{uc.emoji}</span>
                  <div>
                    <p className="font-semibold text-sm">{uc.label}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{uc.desc}</p>
                  </div>
                </SelectCard>
              ))}
            </div>
          </div>
        )}

        {/* ── Step 2: Role ── */}
        {step === 2 && (
          <div>
            <StepHeader step={2} total={TOTAL_STEPS} title="How would you describe yourself?" subtitle="This helps us personalise your experience." />
            <div className="grid gap-3">
              {ROLES.map(r => (
                <SelectCard key={r.id} selected={role === r.id} onClick={() => setRole(r.id)}>
                  <div>
                    <p className="font-semibold text-sm">{r.label}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{r.desc}</p>
                  </div>
                </SelectCard>
              ))}
            </div>
          </div>
        )}

        {/* ── Step 3: Platforms ── */}
        {step === 3 && (
          <div>
            <StepHeader step={3} total={TOTAL_STEPS} title="Which platforms do you want to set up?" subtitle="We've suggested some based on your answers. Adjust as needed." />
            {['Ad Spend', 'Publisher', 'Ecommerce', 'Protection'].map(group => {
              const groupPlatforms = ALL_PLATFORMS.filter(p => p.group === group);
              return (
                <div key={group} className="mb-5">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">{group}</p>
                  <div className="grid gap-2 sm:grid-cols-2">
                    {groupPlatforms.map(p => (
                      <SelectCard key={p.id} selected={platforms.includes(p.id)} onClick={() => togglePlatform(p.id)} multi>
                        <span className="text-xl flex-shrink-0">{p.emoji}</span>
                        <div>
                          <p className="font-semibold text-sm">{p.label}</p>
                          <p className="text-xs text-muted-foreground">{p.desc}</p>
                        </div>
                      </SelectCard>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* ── Step 4: Experience ── */}
        {step === 4 && (
          <div>
            <StepHeader step={4} total={TOTAL_STEPS} title="What's your experience level?" subtitle="We'll adjust onboarding guidance to match." />
            <div className="grid gap-3">
              {EXPERIENCE.map(e => (
                <SelectCard key={e.id} selected={experience === e.id} onClick={() => setExperience(e.id)}>
                  <div>
                    <p className="font-semibold text-sm">{e.label}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{e.desc}</p>
                  </div>
                </SelectCard>
              ))}
            </div>
          </div>
        )}

        {/* ── Step 5: Guided setup ── */}
        {step === 5 && (
          <div>
            <StepHeader step={5} total={TOTAL_STEPS} title="You're all set! Let's connect your first platform." subtitle="Click any platform below to start the setup. You can always come back to this later." />
            <div className="grid gap-3 mb-8">
              {ALL_PLATFORMS.filter(p => platforms.includes(p.id)).map(p => (
                <Link key={p.id} to={platformSetupPath(p.id)}
                  className="flex items-center gap-4 p-4 bg-card border border-border rounded-xl hover:border-primary transition-colors group">
                  <span className="text-2xl flex-shrink-0">{p.emoji}</span>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm">{p.label}</p>
                    <p className="text-xs text-muted-foreground">{p.desc}</p>
                  </div>
                  <span className="text-xs text-primary font-medium opacity-0 group-hover:opacity-100 transition-opacity">Set up →</span>
                  <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0 group-hover:text-primary" />
                </Link>
              ))}
            </div>
            <button onClick={handleFinish} disabled={saving}
              className="w-full flex items-center justify-center gap-2 py-3 bg-primary text-primary-foreground rounded-xl font-medium hover:bg-primary/90 transition-colors disabled:opacity-50">
              {saving ? <div className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Sparkles className="h-4 w-4" />}
              {saving ? 'Setting up...' : 'Go to dashboard →'}
            </button>
          </div>
        )}

        {/* Navigation buttons */}
        {step < 5 && (
          <div className="flex items-center justify-between mt-8">
            {step > 1 ? (
              <button onClick={() => setStep(s => s - 1)} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors">
                <ArrowLeft className="h-4 w-4" /> Back
              </button>
            ) : <div />}
            <button onClick={handleNext} disabled={saving}
              className="flex items-center gap-2 px-6 py-2.5 bg-primary text-primary-foreground rounded-xl font-medium hover:bg-primary/90 transition-colors disabled:opacity-50">
              {saving ? <div className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : null}
              {step === 4 ? 'See my setup plan' : 'Continue'}
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
